import { useMemo, useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import classNames from "classnames";

import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { useQuery } from "@stadium/services/apollo-client";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import ProductDetailIndex from "@components/store/product-detail";
import AvailableProductsList from "@components/store/product-detail/byo/available-products-list";
import ProductListingProvider from "@components/store/products/listing/listing-provider";

import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useComponentLifecycle } from "@stadium/hooks/lib/use-component-lifecycle";

import { getCountry } from "@store/selectors/country";
import { getMenuId } from "@store/selectors/menu";
import { getStoreCurrencyCode, getIsHideBulkPricing } from "@store/selectors/store";

import { isByoProduct, getProductType, getProductCategory } from "@helpers/products/types";
import { fetchProductDetail } from "@graphql/queries/stores/products/fetch-detail";

import { productDetailPageType } from "@constants/page";

import { useEventLocationContext } from "@stadium/services/analytics/context/event-location";
import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";

import styles from "./main.module.scss";

const StoreProductDetail = ({
  productSlug,
  productData: initialProductData,
  cmsSectionIndex = 0,
}) => {
  const router = useRouter();
  const { query } = router;
  const { countryCode } = query;
  const pageCountryCode = countryCode?.split("-")[0];

  const [skipFirstFetch, setSkipFirstFetch] = useState(true);
  const initialPageCountryCodeRef = useRef(pageCountryCode);
  const country = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const {
    isNewSwagmagicHost,
    storeNumber,
    isGlobalCountry,
    setPageState,
    resetPageState,
    getUpdatedCatalogBasePath,
    getUpdatedStoreBasePath,
  } = useStoreContext();
  const menuId = useSelector(getMenuId);
  const { volumeDiscountProductsVariables, isSendToOthers } = useSendItemsContext();
  const { previewMode } = usePreviewModeContext();
  const hideBulkPricing = useSelector(getIsHideBulkPricing);
  const { services, location, baseEventLocationProperties } = useEventLocationContext();
  const { trackPage } = useActionEvents(services);

  const variables = {
    slug: productSlug,
    currency: storeCurrencyCode,
    country: isGlobalCountry ? "" : country.iso,
    ...(isGlobalCountry && { globalZone: isGlobalCountry }),
    storeNumber,
    menuId,
    ...volumeDiscountProductsVariables,
  };

  const { data: queriedData, loading } = useQuery(fetchProductDetail, {
    variables,
    fetchPolicy: "cache-and-network",
    skip: skipFirstFetch || !storeCurrencyCode || !menuId,
  });

  const data = queriedData || initialProductData;

  const isAwaitingCountryRefresh =
    pageCountryCode?.toLowerCase() !== initialPageCountryCodeRef.current?.toLowerCase() &&
    !queriedData;

  useEffect(() => {
    if (
      pageCountryCode &&
      pageCountryCode.toLowerCase() !== initialPageCountryCodeRef.current?.toLowerCase()
    ) {
      setSkipFirstFetch(false);
    }
  }, [pageCountryCode]);

  useEffect(() => {
    if (skipFirstFetch && ((isSendToOthers && hideBulkPricing) || previewMode)) {
      setSkipFirstFetch(false);
    }
  }, [isSendToOthers, storeCurrencyCode]);

  useComponentLifecycle({ onUnmount: resetPageState });

  useEffect(() => {
    setPageState((state) => ({
      ...state,
      showSendItemIcon: true,
      pageType: productDetailPageType,
    }));
  }, []);

  const product = useMemo(() => data?.product, [data]);

  const redirectToMenuRoute = useCallback(
    (countryCode) => {
      const routeToRedirect = isNewSwagmagicHost
        ? getUpdatedCatalogBasePath({ countryCode })
        : getUpdatedStoreBasePath({ countryCode });
      router.replace(routeToRedirect);
    },
    [router, getUpdatedCatalogBasePath, getUpdatedStoreBasePath],
  );

  useEffect(() => {
    if (product?.id && location) {
      trackPage({
        page: location,
        product_id: product.id,
        product_type: getProductType(product),
        category: getProductCategory(product),
        ...baseEventLocationProperties,
      });
    }
  }, [product?.id, location, trackPage, baseEventLocationProperties]);

  useEffect(() => {
    if (!pageCountryCode || loading || !product?.id || isAwaitingCountryRefresh) return;
    if (!product.vendorByCountry) {
      redirectToMenuRoute(pageCountryCode.toLowerCase());
    }
  }, [
    product?.id,
    product?.vendorByCountry,
    pageCountryCode,
    loading,
    redirectToMenuRoute,
    isAwaitingCountryRefresh,
  ]);

  if (loading) return <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-100" />;

  return (
    <div
      className={classNames(styles.fullwidthBg, {
        [styles.byoProductPage]: isByoProduct(product?.intent),
        [styles.withRevamp]: true,
      })}
    >
      <section
        className={classNames(styles.storePageContainer, styles.extraClass, {
          [styles.demoBar]: previewMode,
          [styles.cmsSectionIndex]: cmsSectionIndex !== 0,
        })}
      >
        <ProductDetailIndex product={product} />
      </section>

      {isByoProduct(product.intent) ? (
        <ProductListingProvider>
          <AvailableProductsList product={data.product} />
        </ProductListingProvider>
      ) : null}
    </div>
  );
};

export default StoreProductDetail;
